import { MoreHorizontal } from '@/ui/icons'
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/components'
import { cn } from '@/shared/utils'
import type { VehicleProfile } from '@/shared/api/vehicles'
import { getVehicleSubtitle, getVehicleTitle } from '../utils/vehicleLabels'

const VEHICLE_TYPE_LABELS: Record<VehicleProfile['vehicle_type'], string> = {
  truck: 'Truck',
  suv: 'SUV',
  other: 'Other',
}

const EQUIPMENT: { field: keyof VehicleProfile; label: string }[] = [
  { field: 'has_winch', label: 'Winch' },
  { field: 'has_skid_plates', label: 'Skid plates' },
  { field: 'has_rock_sliders', label: 'Rock sliders' },
  { field: 'has_roof_rack', label: 'Roof rack' },
  { field: 'has_roof_top_tent', label: 'Roof top tent' },
  { field: 'has_truck_camper', label: 'Truck camper' },
]

interface VehicleSpecsProps {
  vehicle: VehicleProfile
  /** Owner actions. Leave these out for a visitor. */
  onEdit?: (vehicle: VehicleProfile) => void
  onSetPrimary?: (vehicle: VehicleProfile) => void
  onDelete?: (vehicle: VehicleProfile) => void
}

export function VehicleSpecs({ vehicle, onEdit, onSetPrimary, onDelete }: VehicleSpecsProps) {
  const subtitle = getVehicleSubtitle(vehicle)
  const facts = [
    VEHICLE_TYPE_LABELS[vehicle.vehicle_type],
    vehicle.is_daily_driver ? 'Daily driver' : null,
    onEdit && vehicle.is_primary ? 'Primary' : null,
  ].filter(Boolean)

  return (
    <Card className="border-border bg-card shadow-none">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="break-words text-2xl font-semibold tracking-tight text-foreground">
              {getVehicleTitle(vehicle)}
            </h2>
            {subtitle && <p className="mt-1 text-sm text-foreground">{subtitle}</p>}
            <p className="mt-1 text-sm text-earth-600">{facts.join(' · ')}</p>
          </div>

          {onEdit && (
            <div className="flex flex-shrink-0 items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onEdit(vehicle)}>
                Edit
              </Button>
              {/* Not modal: a modal menu that opens the delete dialog leaves the page unclickable. */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="px-2" aria-label="More vehicle actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {!vehicle.is_primary && onSetPrimary && (
                    <DropdownMenuItem onClick={() => onSetPrimary(vehicle)}>Make primary</DropdownMenuItem>
                  )}
                  {onDelete && (
                    <DropdownMenuItem onClick={() => onDelete(vehicle)} className="text-error focus:text-error">
                      Delete vehicle
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border pt-5 sm:grid-cols-3">
          {EQUIPMENT.map(({ field, label }) => {
            const hasIt = vehicle[field] === true
            return (
              <li
                key={field}
                className={cn('flex items-center gap-2 text-sm', hasIt ? 'font-medium text-foreground' : 'text-earth-600')}
              >
                <span
                  aria-hidden
                  className={cn(
                    'h-2 w-2 flex-shrink-0 rounded-full border',
                    hasIt ? 'border-adventure-500 bg-adventure-500' : 'border-earth-400'
                  )}
                />
                <span>{label}</span>
                <span className="sr-only">{hasIt ? 'fitted' : 'not fitted'}</span>
              </li>
            )
          })}
        </ul>

        {vehicle.notes && (
          <div className="mt-5 border-t border-border pt-5">
            <h3 className="text-sm font-medium text-foreground">
              Notes {onEdit && <span className="font-normal text-earth-600">· Only you can see these</span>}
            </h3>
            <p className="mt-1 whitespace-pre-line break-words text-sm text-foreground">{vehicle.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
